import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { dateTime } from "@/lib/dates";
import { api, uploadErrorMessage, uploadFile } from "@/lib/api";
import {
  FileText, Loader2, RefreshCw, Download, Trash2, AlertTriangle, Check, Clock, X, Plus,
} from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";
import { ATENA_MODELS } from "@/lib/atenaModels";
import { useEscape } from "@/hooks/useEscape";
import FilePicker from "@/components/FilePicker";

// The screen a generation is answerable on.
//
// Before this, a run left one trace — the document, if it worked. A failure at
// the fourth minute had nowhere to be seen and nothing to retry from. So the
// list is of runs, not of files: a failed run is a row here, with the stage it
// died at and the error it died of.

const STATUS = {
  queued: { label: "În așteptare", icon: Clock, tone: "text-fg/50" },
  processing: { label: "În lucru", icon: Loader2, tone: "text-brand", spin: true },
  done: { label: "Gata", icon: Check, tone: "text-emerald-500" },
  failed: { label: "Eșuat", icon: AlertTriangle, tone: "text-red-400" },
  // Cine a văzut că e al patrulea și a decis să nu aștepte n-a eșuat.
  cancelled: { label: "Anulat", icon: X, tone: "text-fg/40" },
};

// The local default for a run started from this screen. Atena's own path keeps
// its Claude-then-GPT chain: she answers questions, this files documents, and
// the person filing one gets to say whether it leaves the building.
const DEFAULT_LOCAL_MODEL = "ollama:qwen2.5:7b";

const SOURCE_LABEL = {
  file: "fișier", channel: "canal", board: "board", canvas: "whiteboard", text: "text",
};

// "intern" is the one that matters: it says the content never left the machine.
const PROVIDER = {
  intern: { label: "intern", title: "Model local — conținutul nu a părăsit serverul" },
  anthropic: { label: "Anthropic", title: "Claude — conținutul a fost trimis la Anthropic" },
  openai: { label: "OpenAI", title: "GPT — conținutul a fost trimis la OpenAI" },
};

const mb = (n) => `${Math.round((n || 0) / (1024 * 1024))} MB`;

// Etichetele limbilor vin de la server (documents.LANGUAGES). Astea sunt doar
// numele scurte pentru insigna din listă, unde nu încape „română și engleză".
const LANGUAGE_BADGE = { ro: "RO", en: "EN", both: "RO+EN" };

const duration = (ms) => {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return ms < 60000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
};

const when = (iso) => dateTime(iso) || "—";

function StatusBadge({ status, queuePosition }) {
  const s = STATUS[status] || STATUS.queued;
  const Icon = s.icon;
  // La 109 minute de transcriere, „În lucru" și „al treilea la rând" sunt două
  // situații diferite, iar a doua înseamnă că poți pleca de lângă ecran.
  const waiting = queuePosition > 0;
  const label = waiting ? `La rând (${queuePosition})` : s.label;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs ${waiting ? "text-fg/50" : s.tone}`}
          data-testid={waiting ? "status-queued-position" : `status-${status}`}>
      <Icon className={`w-3.5 h-3.5 ${s.spin && !waiting ? "animate-spin" : ""}`} />
      {label}
    </span>
  );
}

export default function DocumentsView({ workspaceId, currentUser, channels = [], boards = [],
                                        canvases = [], subscribe }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [sourceKind, setSourceKind] = useState("");
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(null);   // {kind: "delete"|"regenerate", run}
  const [model, setModel] = useState("");
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ template: "summary", kind: "channel", id: "",
                                      format: "docx", title: "", model: "", language: "ro",
                                      instructions: "", speech_model: "", frame_model: "" });
  const [templates, setTemplates] = useState([]);
  const [sources, setSources] = useState([]);
  const [formats, setFormats] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [imageFormats, setImageFormats] = useState([]);
  const [speech, setSpeech] = useState(null);
  const [frames, setFrames] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sent, setSent] = useState(null);       // {loaded, total} — cifra care se mișcă
  const [maxUpload, setMaxUpload] = useState(0);
  const pollRef = useRef(null);
  const fileInput = useRef(null);

  useEscape(creating, () => setCreating(false));
  useEscape(!!openId && !creating && !confirming, () => setOpenId(null));

  const cancel = async (runId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${runId}/cancel`,
                                      {}, { params: { workspace_id: workspaceId } });
      toast.success(`Scos de la rând (era al ${data.was_at}-lea).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anularea nu a reușit");
    } finally { setBusy(false); }
  };

  const load = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const { data } = await api.get("/documents", {
        params: { workspace_id: workspaceId, status: status || undefined,
                  source_kind: sourceKind || undefined, limit: 50 },
      });
      setRuns(data.documents || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lista nu s-a putut încărca");
    } finally { setLoading(false); }
  }, [workspaceId, status, sourceKind]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!workspaceId) return;
    // Templates, sources and formats all come from the server. A second list
    // here is how "file" ended up in the filter and not in the dialog: you
    // could filter by a source you could not start from.
    api.get("/media/capabilities", { params: { workspace_id: workspaceId } })
      .then((r) => {
        setTemplates(r.data?.templates || []);
        setSources(r.data?.sources || []);
        setFormats(r.data?.formats || []);
        setLanguages(r.data?.languages || []);
        setImageFormats(r.data?.formats_with_images || []);
        setSpeech(r.data?.speech || null);
        setFrames(r.data?.frames || null);
        setMaxUpload(r.data?.upload?.max_bytes || 0);
      })
      .catch(() => { setTemplates([]); setSources([]); setFormats([]); setLanguages([]); });
  }, [workspaceId]);

  const loadFiles = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const { data } = await api.get("/files", { params: { workspace_id: workspaceId, limit: 100 } });
      setFiles(data.files || []);
    } catch { setFiles([]); }
  }, [workspaceId]);

  useEffect(() => { if (creating) loadFiles(); }, [creating, loadFiles]);

  const upload = async (file) => {
    if (!file) return;
    // Refused here, before the wait. A 300 MB file that is going to be rejected
    // should be rejected in the first second, not after minutes of a progress
    // bar — and the refusal has to carry both numbers.
    if (maxUpload && file.size > maxUpload) {
      toast.error(`«${file.name}» are ${mb(file.size)}, maximul este ${mb(maxUpload)}.`);
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      // Through uploadFile, which is the one path with a timeout exception and
      // progress. The hand-rolled version here posted to /files — a GET route —
      // so it answered 405 for every file at every size, and had never worked.
      const data = await uploadFile(workspaceId, file, (percent, bytes) => {
        setProgress(percent);
        setSent(bytes || null);
      });
      await loadFiles();
      setDraft((d) => ({ ...d, id: data.id }));
      toast.success(`${data.name} încărcat`);
    } catch (e) {
      toast.error(uploadErrorMessage(e, maxUpload, file.size));
    } finally { setUploading(false); setProgress(0); setSent(null); }
  };

  // Live, and polled while anything is still moving: the websocket carries each
  // transition, and the poll is the net for a socket that dropped mid-run.
  useEffect(() => {
    if (!subscribe) return undefined;
    return subscribe((ev) => {
      if (ev.type === "document_run" && ev.run?.workspace_id === workspaceId) {
        setRuns((prev) => {
          const rest = prev.filter((r) => r.id !== ev.run.id);
          return [ev.run, ...rest].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        });
        setDetail((d) => (d && d.id === ev.run.id ? { ...d, ...ev.run } : d));
      } else if (ev.type === "document_deleted") {
        setRuns((prev) => prev.filter((r) => r.id !== ev.run_id));
        setOpenId((id) => (id === ev.run_id ? null : id));
      }
    });
  }, [subscribe, workspaceId]);

  // Capturile nu depind de șablon: orice document dintr-un film le poartă.
  // Markdown nu poate purta o poză într-un singur fișier, deci alegerea lui e o
  // alegere de a livra documentul fără ele — se spune înainte, nu se descoperă
  // la deschidere. Ovidiu a nimerit Raport + MD și a primit un document fără
  // poze, fără ca ceva să-i spună că există și cealaltă cale.
  const stepTemplate = useMemo(
    () => (templates.find((t) => t.key === draft.template) || {}).shape === "steps",
    [templates, draft.template]);
  const chosenFile = useMemo(
    () => (draft.kind === "file" ? files.find((f) => f.id === draft.id) : null) || null,
    [draft.kind, draft.id, files]);
  const fromVideo = (chosenFile?.media_kind === "video");
  // Un fișier audio se transcrie la fel, dar n-are cadre: alegerea modelului îl
  // privește, secțiunea de capturi nu.
  const needsSpeech = fromVideo || chosenFile?.media_kind === "audio";
  const willHavePictures = fromVideo || stepTemplate;
  const formatDropsImages = useMemo(
    () => willHavePictures && imageFormats.length > 0 && !imageFormats.includes(draft.format),
    [willHavePictures, imageFormats, draft.format]);

  // Alegerea unui film mută formatul pe unul care poate purta capturile. Nu
  // tăcut: nota de sub selector spune de ce s-a mutat.
  useEffect(() => {
    if (!fromVideo || imageFormats.length === 0) return;
    setDraft((d) => (imageFormats.includes(d.format) ? d : { ...d, format: imageFormats[0] }));
  }, [fromVideo, imageFormats]);

  const moving = useMemo(() => runs.some((r) => r.status === "queued" || r.status === "processing"), [runs]);
  useEffect(() => {
    clearInterval(pollRef.current);
    if (!moving) return undefined;
    pollRef.current = setInterval(load, 4000);
    return () => clearInterval(pollRef.current);
  }, [moving, load]);

  useEffect(() => {
    if (!openId) { setDetail(null); return undefined; }
    // Cleared first. Without this the panel kept the previously opened run
    // until the fetch returned — showing its stages, its error and its enabled
    // Descarcă under the new run's row. Somebody clicking quickly downloaded
    // the wrong document, or hit a 409 on a run that had never produced one.
    let current = true;
    setDetail(null);
    api.get(`/documents/${openId}`, { params: { workspace_id: workspaceId } })
      .then((r) => { if (current) setDetail(r.data); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Documentul nu s-a putut încărca"));
    return () => { current = false; };
  }, [openId, workspaceId]);

  const download = async (run) => {
    try {
      // Through the api client, so the bearer token travels in a header. A URL
      // is copied, logged by proxies and pasted into chats; a token in one is a
      // credential with a long tail.
      const res = await api.get(`/documents/${run.id}/download`, {
        params: { workspace_id: workspaceId }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = run.file_name || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Descărcarea a eșuat");
    }
  };

  const regenerate = async () => {
    const run = confirming?.run;
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${run.id}/regenerate`,
        // Empty means "the same as before", and the same as before is this
        // run's model — not the server's chain, which costs money.
        { model: model || run.model || DEFAULT_LOCAL_MODEL },
        { params: { workspace_id: workspaceId } });
      setConfirming(null);
      setOpenId(data.id);
      await load();
      toast.success("Regenerare pornită");
    } catch (e) {
      // 409 carries the reason the source is gone. Showing it is the point.
      toast.error(e?.response?.data?.detail || "Regenerarea nu a pornit");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    const run = confirming?.run;
    setBusy(true);
    try {
      await api.delete(`/documents/${run.id}`, { params: { workspace_id: workspaceId } });
      setConfirming(null);
      setOpenId((id) => (id === run.id ? null : id));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ștergerea a eșuat");
    } finally { setBusy(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const source = draft.kind === "text"
        ? { kind: "text", text: draft.id, name: "text" }
        : { kind: draft.kind, id: draft.id };
      const { data } = await api.post("/documents", {
        template: draft.template, source, format: draft.format,
        language: draft.language, instructions: draft.instructions,
        speech_model: draft.speech_model || undefined,
        frame_model: draft.frame_model || undefined,
        title: draft.title || undefined,
        model: draft.model || DEFAULT_LOCAL_MODEL,
      }, { params: { workspace_id: workspaceId } });
      setCreating(false);
      setOpenId(data.id);
      await load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Generarea nu a pornit");
    } finally { setBusy(false); }
  };

  const sourceOptions = draft.kind === "channel" ? channels
    : draft.kind === "board" ? boards : draft.kind === "canvas" ? canvases : [];

  return (
    <div className="h-full flex min-h-0" data-testid="documents-view">
      <div className="flex-1 flex flex-col min-h-0">
        <div className="min-h-14 px-4 py-2 flex items-center gap-3 border-b border-line/10 flex-wrap">
          <FileText className="w-4 h-4 text-brand" />
          <h2 className="font-bold tracking-tight">Documente</h2>
          <span className="text-fg/40 text-xs mono">{runs.length}</span>
          <div className="ml-auto flex items-center gap-2">
            <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}
              className="input-flat input-inline text-xs py-1" aria-label="Filtru stare">
              <option value="">Toate stările</option>
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select data-testid="filter-source" value={sourceKind} onChange={(e) => setSourceKind(e.target.value)}
              className="input-flat input-inline text-xs py-1" aria-label="Filtru tip sursă">
              <option value="">Toate sursele</option>
              {Object.entries(SOURCE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <button type="button" data-testid="new-document" onClick={() => setCreating(true)}
              className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Generează
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {loading && <p className="p-4 text-sm text-fg-muted">Se încarcă…</p>}
          {!loading && runs.length === 0 && (
            <p className="p-4 text-sm text-fg-muted" data-testid="documents-empty">
              Niciun document încă. „Generează" pornește unul dintr-un canal, board, whiteboard sau fișier.
            </p>
          )}
          {runs.map((run) => (
            <button
              key={run.id} type="button" data-testid={`document-row-${run.id}`}
              onClick={() => setOpenId(run.id)}
              className={`w-full text-left px-4 py-3 border-b border-line/5 hover:bg-fg/5 flex items-center gap-3 ${
                openId === run.id ? "bg-fg/5" : ""}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm truncate">{run.title || "(fără titlu)"}</p>
                <p className="text-xs text-fg/50 truncate">
                  {SOURCE_LABEL[run.source_kind] || run.source_kind} · {run.template} · {run.requested_by_name} · {when(run.created_at)}
                  {/* Two runs with the same title are not two independent
                      attempts if one came from the other, and the payload has
                      always said which. */}
                  {run.regenerated_from && (
                    <span data-testid={`lineage-${run.id}`}> · regenerare</span>
                  )}
                  {run.regenerations > 0 && (
                    <span data-testid={`regen-count-${run.id}`}>
                      {" "}· regenerat de {run.regenerations}{run.regenerations === 1 ? " dată" : " ori"}
                    </span>
                  )}
                </p>
              </div>
              {run.provider && (
                <span title={PROVIDER[run.provider]?.title} data-testid={`provider-${run.id}`}
                  className="text-[10px] mono px-1.5 py-0.5 border border-line/20 text-fg/60 flex-shrink-0">
                  {PROVIDER[run.provider]?.label || run.provider}
                </span>
              )}
              <span className="text-xs text-fg/40 mono flex-shrink-0 w-14 text-right">{duration(run.duration_ms)}</span>
              <span className="flex-shrink-0 w-28">
                <StatusBadge status={run.status} queuePosition={run.queue_position} />
              </span>
            </button>
          ))}
        </div>
      </div>

      {openId && !detail && (
        <aside className="w-[26rem] max-w-[90vw] border-l border-line/10 bg-surface flex items-center justify-center"
               data-testid="document-detail-loading">
          <Loader2 className="w-4 h-4 animate-spin text-fg/40" />
        </aside>
      )}

      {detail && (
        <aside className="w-[26rem] max-w-[90vw] border-l border-line/10 bg-surface flex flex-col min-h-0"
               data-testid="document-detail">
          <div className="px-4 py-3 border-b border-line/10 flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm truncate">{detail.title || "(fără titlu)"}</p>
              <p className="text-xs text-fg/50" data-testid="detail-meta">
                {detail.template} · {detail.format}
                {detail.language && detail.language !== "ro" && ` · ${LANGUAGE_BADGE[detail.language] || detail.language}`}
                {" · "}{when(detail.created_at)}
              </p>
            </div>
            <button type="button" onClick={() => setOpenId(null)} aria-label="Închide"
              className="text-fg/40 hover:text-fg p-1"><X className="w-4 h-4" /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
            {(detail.regenerated_from || detail.regenerations > 0) && (
              <div className="text-xs text-fg/60 space-y-1" data-testid="document-lineage">
                {detail.regenerated_from && (
                  <p>
                    Regenerare a{" "}
                    <button type="button" data-testid="open-origin"
                      onClick={() => setOpenId(detail.regenerated_from)}
                      className="underline hover:text-fg">rulării anterioare</button>.
                  </p>
                )}
                {detail.regenerations > 0 && (
                  <p>Regenerată de {detail.regenerations}
                    {detail.regenerations === 1 ? " dată" : " ori"}.</p>
                )}
              </div>
            )}

            <div className="flex items-center gap-3">
              <StatusBadge status={detail.status} queuePosition={detail.queue_position} />
              <span className="text-xs text-fg/50">total {duration(detail.duration_ms)}</span>
              {detail.model && <span className="text-xs text-fg/50 mono truncate">{detail.model}</span>}
            </div>

            {detail.queue_label && (
              /* Propoziția, nu numărul. Vine de la server (docruns.queue_label),
                 ca interfața să nu aibă a doua formulare a aceleiași stări. */
              <div className="border border-line/20 bg-surface-2 p-3 space-y-2" data-testid="document-queue">
                <p className="text-xs text-fg/70">{detail.queue_label}</p>
                {detail.queue_position > 0 && detail.queue_kind === "transcribe" && (
                  <p className="text-[11px] text-fg/40">
                    Transcrierea rulează una câte una: o oră de înregistrare înseamnă
                    în jur de două ore și un sfert de procesor.
                  </p>
                )}
                {detail.queue_position > 0 && detail.queue_kind === "generate" && (
                  <p className="text-[11px] text-fg/40">
                    Modelul scrie un document o dată. Cât stai la rând nu se scade din
                    timpul tău de scriere.
                  </p>
                )}
                {detail.queue_position > 0 && (
                  /* Singurul lucru pe care o coadă vizibilă îl permite și una
                     invizibilă nu: să pleci. */
                  <button type="button" data-testid="document-cancel" disabled={busy}
                    onClick={() => cancel(detail.id)}
                    className="btn-ghost text-xs px-3 py-1.5 w-full disabled:opacity-40">
                    Nu aștept — scoate-mă de la rând
                  </button>
                )}
              </div>
            )}

            {detail.instructions && (
              /* Ce a cerut omul, vizibil pe rulare. Fără el, două rulări din
                 aceeași sursă cu rezultate diferite arată inexplicabile. */
              <div data-testid="document-instructions">
                <p className="label-tiny mb-2">// CE S-A CERUT</p>
                <p className="text-xs text-fg/70 whitespace-pre-wrap break-words">{detail.instructions}</p>
              </div>
            )}

            {detail.error && (
              <div className="border border-red-500/30 bg-red-500/5 p-3" data-testid="document-error">
                <p className="text-xs font-semibold text-red-400 mb-1">
                  A eșuat la etapa „{detail.stages?.find((s) => s.name === detail.error.stage)?.label || detail.error.stage}"
                </p>
                {/* The real message, whole. Replacing it with a stock phrase is
                    the thing this whole screen exists to avoid. */}
                <p className="text-xs text-fg/80 break-words mono">{detail.error.message}</p>
              </div>
            )}

            {detail.stages?.length > 0 && (
              <div data-testid="document-stages">
                <p className="label-tiny mb-2">// ETAPE</p>
                <ul className="space-y-1">
                  {detail.stages.map((s, i) => (
                    <li key={`${s.name}-${i}`} className="flex items-baseline gap-2 text-xs">
                      <span className={s.ok ? "text-emerald-500" : "text-red-400"}>{s.ok ? "✓" : "✕"}</span>
                      <span className="flex-1 truncate">{s.label}</span>
                      <span className="mono text-fg/50">{duration(s.ms)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {detail.warnings?.length > 0 && (
              <ul className="space-y-1" data-testid="document-warnings">
                {detail.warnings.map((w, i) => (
                  <li key={i} className="text-xs text-amber-400/90 flex gap-1.5">
                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />{w}
                  </li>
                ))}
              </ul>
            )}

            <div>
              <p className="label-tiny mb-1">// MODEL PENTRU REGENERARE</p>
              {/* "Ca înainte" has to mean the model this run actually used. It
                  said "Claude, apoi GPT" on a run written locally, so pressing
                  Regenerează on the default spent Claude credits on somebody
                  who had deliberately chosen not to. */}
              <select data-testid="regenerate-model" value={model} onChange={(e) => setModel(e.target.value)}
                className="input-flat text-xs py-1 w-full" aria-label="Model">
                {/* A run that failed at "Citire sursă" never reached a model,
                    so "ca înainte" has no meaning — and defaulting to the
                    server's chain sends the most likely thing to regenerate,
                    a failed run, quietly to a paid model. */}
                <option value="">
                  {detail.model
                    ? `Același ca înainte (${detail.model})`
                    : `Model local (${DEFAULT_LOCAL_MODEL})`}
                </option>
                {ATENA_MODELS.filter((m) => m.value).map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="p-3 border-t border-line/10 space-y-2">
            <div className="flex gap-2">
              <button type="button" data-testid="document-download" disabled={detail.status !== "done"}
                onClick={() => download(detail)}
                title={detail.status === "done" ? undefined
                  : "Rularea nu s-a terminat cu bine, deci nu există fișier."}
                className="btn-ghost text-xs px-3 py-1.5 flex-1 flex items-center justify-center gap-1.5 disabled:opacity-40">
                <Download className="w-3.5 h-3.5" /> Descarcă
              </button>
              <button type="button" data-testid="document-regenerate"
                onClick={() => setConfirming({ kind: "regenerate", run: detail })}
                className="btn-primary text-xs px-3 py-1.5 flex-1 flex items-center justify-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" /> Regenerează
              </button>
            </div>
            {/* On its own line, with a word on it. It was an unlabelled icon
                ninety pixels from the primary action, and it deletes for good. */}
            <button type="button" data-testid="document-delete"
              onClick={() => setConfirming({ kind: "delete", run: detail })}
              className="w-full text-xs px-3 py-1.5 border border-line/20 text-fg/40 hover:text-red-400 hover:border-red-400/40 flex items-center justify-center gap-1.5">
              <Trash2 className="w-3.5 h-3.5" /> Șterge documentul
            </button>
          </div>
        </aside>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
             onClick={() => setCreating(false)}>
          <form onSubmit={create} onClick={(e) => e.stopPropagation()}
                data-testid="document-form"
                className="bg-surface border border-line/10 w-full max-w-md p-4 space-y-3">
            <h3 className="font-bold tracking-tight">Generează un document</h3>
            <label className="block text-xs text-fg-muted space-y-1">
              <span>Șablon</span>
              <select data-testid="draft-template" value={draft.template} className="input-flat text-sm w-full"
                onChange={(e) => setDraft({ ...draft, template: e.target.value })}>
                {templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
            <label className="block text-xs text-fg-muted space-y-1">
              <span>Sursă</span>
              <select data-testid="draft-kind" value={draft.kind} className="input-flat text-sm w-full"
                onChange={(e) => setDraft({ ...draft, kind: e.target.value, id: "" })}>
                {sources.map((s) => <option key={s.kind} value={s.kind}>{s.label}</option>)}
              </select>
            </label>
            {draft.kind === "text" && (
              <textarea data-testid="draft-text" rows={4} value={draft.id} placeholder="Textul sursă"
                onChange={(e) => setDraft({ ...draft, id: e.target.value })}
                className="input-flat text-sm w-full" />
            )}
            {draft.kind === "file" && (
              <div className="space-y-2" data-testid="draft-file">
                {/* A searchable list, not a <select>: an <option> holds text and
                    nothing else, so seven files called snip1.png render as seven
                    identical rows. Unreadable files stay visible and disabled —
                    somebody looking for the recording they uploaded needs to see
                    it here, with the reason, not to wonder where it went. */}
                <FilePicker files={files} value={draft.id}
                  onChange={(id) => setDraft({ ...draft, id })}
                  emptyLabel="Niciun fișier în workspace încă — încarcă unul." />
                <input ref={fileInput} type="file" className="hidden" data-testid="draft-upload-input"
                  onChange={(e) => upload(e.target.files?.[0])} />
                <button type="button" data-testid="draft-upload" disabled={uploading}
                  onClick={() => fileInput.current?.click()}
                  className="btn-ghost text-xs px-3 py-1.5 w-full flex items-center justify-center gap-1.5 disabled:opacity-50">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  {uploading ? `Se încarcă… ${progress}%` : "Încarcă un fișier nou"}
                </button>
                {/* Written before the click, not after the wait. */}
                {maxUpload > 0 && !uploading && (
                  <p className="text-[11px] text-fg/40" data-testid="upload-limit">
                    Maxim {mb(maxUpload)} per fișier.
                  </p>
                )}
                {uploading && (
                  <>
                    <div className="h-1 bg-surface-2" data-testid="upload-progress"
                      role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
                      <div className="h-full bg-brand transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    {/* Pe o jumătate de gigaoctet, procentul se mișcă din minut
                        în minut. Octeții se mișcă vizibil, și asta e diferența
                        dintre „merge" și „s-a blocat". */}
                    {sent?.total > 0 && (
                      <p className="text-[11px] text-fg/40" data-testid="upload-sent">
                        {mb(sent.loaded)} din {mb(sent.total)} trimiși
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
            {draft.kind !== "text" && draft.kind !== "file" && (
              <select data-testid="draft-source" value={draft.id} className="input-flat text-sm w-full"
                onChange={(e) => setDraft({ ...draft, id: e.target.value })}>
                <option value="">Alege…</option>
                {sourceOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            )}
            <div className="flex gap-2">
              <label className="block text-xs text-fg-muted space-y-1 flex-1">
                <span>Format</span>
                <select data-testid="draft-format" value={draft.format} className="input-flat text-sm w-full"
                  onChange={(e) => setDraft({ ...draft, format: e.target.value })}>
                  {(formats.length ? formats : ["docx"]).map((f) =>
                    <option key={f} value={f}>{f.toUpperCase()}</option>)}
                </select>
              </label>
              <label className="block text-xs text-fg-muted space-y-1 flex-[2]">
                <span>Titlu (opțional)</span>
                <input data-testid="draft-title" value={draft.title} className="input-flat text-sm w-full"
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
              </label>
            </div>
            {needsSpeech && speech?.models?.length > 1 && (
              <label className="block text-xs text-fg-muted space-y-1">
                <span>{fromVideo ? "Cine transcrie filmul" : "Cine transcrie înregistrarea"}</span>
                {/* Alegerea contează la o oră de film: 24 de minute față de
                    109. Lista vine de la server — interfața n-are voie să aibă
                    a doua listă de modele. */}
                <select data-testid="draft-speech-model" value={draft.speech_model}
                  className="input-flat text-sm w-full"
                  onChange={(e) => setDraft({ ...draft, speech_model: e.target.value })}>
                  <option value="">
                    {speech.models.find((m) => m.default)?.label || "implicit"} — implicit
                  </option>
                  {speech.models.filter((m) => !m.default).map((m) => (
                    <option key={m.name} value={m.name}>{m.label}</option>
                  ))}
                </select>
                <span className="text-[11px] text-fg/40 block" data-testid="speech-model-note">
                  {(speech.models.find((m) => m.name === draft.speech_model)
                    || speech.models.find((m) => m.default) || {}).note}
                </span>
              </label>
            )}
            {fromVideo && frames?.models?.length > 1 && (
              <label className="block text-xs text-fg-muted space-y-1">
                <span>Cine citește capturile, dacă filmul n-are vorbire</span>
                {/* Măsurat pe 36 de capturi reale: local costă 35 de minute și
                    scrie greșit textul de pe ecran; Claude costă 1,7 minute și
                    ~0,17 $ și îl citește exact. Local e implicit — o ciornă nu
                    merită bani. Lista vine de la server. */}
                <select data-testid="draft-frame-model" value={draft.frame_model}
                  className="input-flat text-sm w-full"
                  onChange={(e) => setDraft({ ...draft, frame_model: e.target.value })}>
                  <option value="">{frames.model} — implicit</option>
                  {frames.models.filter((m) => m.name !== frames.model).map((m) => (
                    <option key={m.name} value={m.name}>{m.name}</option>
                  ))}
                </select>
                <span className="text-[11px] text-fg/40 block" data-testid="frame-model-note">
                  {(frames.models.find((m) => m.name === (draft.frame_model || frames.model))
                    || {}).note}
                </span>
              </label>
            )}
            <label className="block text-xs text-fg-muted space-y-1">
              <span>Ce vrei de la document (opțional)</span>
              {/* Cinci șabloane fixe nu acoperă cazurile reale: de cele mai
                  multe ori cineva vrea ceva specific. Textul ajunge la model ca
                  cerere despre conținut, nu ca reguli — aceeași graniță ca la
                  descrierea unui tichet trimis unui agent. */}
              <textarea data-testid="draft-instructions" rows={3} value={draft.instructions}
                maxLength={2000}
                placeholder="ex: accent pe riscurile fiscale; scrie pentru cineva care nu a participat; grupează pe module"
                onChange={(e) => setDraft({ ...draft, instructions: e.target.value })}
                className="input-flat text-sm w-full" />
            </label>
            <label className="block text-xs text-fg-muted space-y-1">
              <span>Cine îl scrie</span>
              {/* Chosen up front, not after a failure: the reason to reach for a
                  frontier model is a heavy file, and that is known before the
                  first run, not after it. Local by default — the content stays
                  on this server unless somebody decides otherwise. */}
              <select data-testid="draft-model" value={draft.model} className="input-flat text-sm w-full"
                onChange={(e) => setDraft({ ...draft, model: e.target.value })}>
                <option value="">Model local (conținutul nu pleacă de pe server)</option>
                {ATENA_MODELS.filter((m) => m.value && !m.value.startsWith("ollama")).map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-xs text-fg-muted space-y-1">
              <span>Limbă</span>
              {/* „Ambele" nu înseamnă două documente: aceleași capturi o singură
                  dată, cu textul în ambele limbi sub fiecare pas. Două fișiere ar
                  fi însemnat aceleași zeci de poze stocate de două ori. */}
              <select data-testid="draft-language" value={draft.language} className="input-flat text-sm w-full"
                onChange={(e) => setDraft({ ...draft, language: e.target.value })}>
                {(languages.length ? languages : [{ key: "ro", label: "română" }]).map((l) => (
                  <option key={l.key} value={l.key}>{l.label}</option>
                ))}
              </select>
            </label>
            {formatDropsImages && (
              <p className="text-[11px] text-amber-400/90" data-testid="draft-format-note">
                {draft.format.toUpperCase()} nu poate purta imagini, așa că documentul va ieși
                fără capturi. Alege {imageFormats.map((f) => f.toUpperCase()).join(" sau ")}.
              </p>
            )}
            {fromVideo && !formatDropsImages && (
              <p className="text-[11px] text-fg/40" data-testid="draft-video-note">
                Din film ies transcrierea și capturi de ecran la momentele în care se
                schimbă imaginea. Ambele intră în document, indiferent de șablon.
              </p>
            )}
            {draft.kind === "file" && speech && !speech.available && (
              <p className="text-[11px] text-fg/40" data-testid="draft-speech-note">
                Fișierele audio și video nu pot fi folosite acum: {speech.reason}.
              </p>
            )}
            {draft.model === "" && (
              <p className="text-[11px] text-amber-400/90" data-testid="draft-local-note">
                Un model local e mai puțin exact pe surse lungi. Pentru un fișier greu,
                alege Claude sau GPT.
              </p>
            )}
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setCreating(false)} className="btn-ghost text-sm px-3 py-1.5 flex-1">Anulează</button>
              <button type="submit" data-testid="draft-submit" disabled={busy || !draft.id}
                className="btn-primary text-sm px-3 py-1.5 flex-1 disabled:opacity-40">Generează</button>
            </div>
          </form>
        </div>
      )}

      {/* Confirmations use the app's dialog, never window.confirm: a native one
          cannot be styled, cannot be tested, and blocks the whole tab. */}
      <ConfirmDialog
        open={confirming?.kind === "regenerate"} onOpenChange={() => setConfirming(null)}
        title="Regenerezi documentul?" busy={busy}
        description={"Pornește o rulare nouă din aceeași sursă cu "
          + (model || confirming?.run?.model || DEFAULT_LOCAL_MODEL)
          + ". Documentul existent rămâne."}
        confirmLabel="Regenerează" onConfirm={regenerate} testId="confirm-regenerate" />
      <ConfirmDialog
        open={confirming?.kind === "delete"} onOpenChange={() => setConfirming(null)}
        title="Ștergi documentul?" destructive busy={busy}
        description="Rularea și fișierul generat dispar pentru toată lumea. Nu se poate anula."
        confirmLabel="Șterge" onConfirm={remove} testId="confirm-delete-document" />
    </div>
  );
}
